import { useState, useEffect, useContext } from "react";
import { Plus, Trash2, Edit2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { trpc } from "../../trpc";
import MerchantContext from "../../contexts/MerchantContext";

const STAGE_LABELS: Record<string, string> = {
  new: "Nuevo",
  pending: "Pendiente",
  success: "Éxito",
  failure: "Fallido",
};

const STAGE_ORDER = ["new", "pending", "success", "failure"] as const;

type TicketPosition = {
  id: string;
  stage: string;
  status: string;
  displayName: string;
  isSystem: boolean;
  order: number;
};

type EditPosition = {
  id: string;
  displayName: string;
  isSystem?: boolean;
  isNew?: boolean;
};

type TicketPositionBlockProps = {
  pipelineId: string;
};

const STAGE_BORDER_COLORS: Record<string, string> = {
  pending: "border-blue-200",
  success: "border-green-200",
  failure: "border-red-200",
};

const STAGE_ADD_BUTTON_CLASSES: Record<string, string> = {
  pending: "border-blue-300 text-blue-700 hover:bg-blue-50",
  success: "border-green-300 text-green-700 hover:bg-green-50",
  failure: "border-red-300 text-red-700 hover:bg-red-50",
};

const TicketPositionBlock = ({ pipelineId }: TicketPositionBlockProps) => {
  const { merchantId } = useContext(MerchantContext);

  const { data: positionsByStage, isLoading } = trpc.pipeline.getTicketPositions.useQuery(
    { merchantId: merchantId || "", pipelineId },
    { enabled: !!merchantId }
  );

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editPositions, setEditPositions] = useState<{
    pending: EditPosition[];
    success: EditPosition[];
    failure: EditPosition[];
  }>({ pending: [], success: [], failure: [] });

  const toEditPositions = (stage: "pending" | "success" | "failure"): EditPosition[] =>
    ((positionsByStage?.[stage] as TicketPosition[] | undefined) ?? []).map((p) => ({
      id: p.id,
      displayName: p.displayName,
      isSystem: p.isSystem,
      isNew: false,
    }));

  useEffect(() => {
    if (positionsByStage) {
      setEditPositions({
        pending: toEditPositions("pending"),
        success: toEditPositions("success"),
        failure: toEditPositions("failure"),
      });
    }
  }, [positionsByStage]);

  const handleStartEdit = () => {
    setEditPositions({
      pending: toEditPositions("pending"),
      success: toEditPositions("success"),
      failure: toEditPositions("failure"),
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditPositions({
      pending: toEditPositions("pending"),
      success: toEditPositions("success"),
      failure: toEditPositions("failure"),
    });
    setIsEditing(false);
  };

  const utils = trpc.useUtils();
  const addTicketPositionMutation = trpc.pipeline.addTicketPosition.useMutation();
  const removeTicketPositionMutation = trpc.pipeline.removeTicketPosition.useMutation();
  const renameTicketPositionMutation = trpc.pipeline.renameTicketPosition.useMutation();

  const handleSaveEdit = async () => {
    if (!merchantId) return;
    setIsSaving(true);
    try {
      const stages = ["pending", "success", "failure"] as const;
      for (const stage of stages) {
        const original = toEditPositions(stage);
        const current = editPositions[stage];

        const toRemove = original.filter((o) => !current.some((c) => c.id === o.id));
        const toRename = original.filter((o) => {
          const c = current.find((x) => x.id === o.id);
          return c && c.displayName !== o.displayName;
        });
        const toAdd = current.filter((c) => c.isNew && c.displayName.trim());

        for (const r of toRemove) {
          await removeTicketPositionMutation.mutateAsync({
            merchantId,
            pipelineId,
            positionId: r.id,
          });
        }
        for (const r of toRename) {
          const c = current.find((x) => x.id === r.id);
          if (c) {
            await renameTicketPositionMutation.mutateAsync({
              merchantId,
              pipelineId,
              positionId: r.id,
              displayName: c.displayName,
            });
          }
        }
        for (const a of toAdd) {
          const status = a.id.trim() || a.displayName.toLowerCase().replace(/\s+/g, "_");
          await addTicketPositionMutation.mutateAsync({
            merchantId,
            pipelineId,
            stage,
            displayName: a.displayName.trim(),
            positionId: status || undefined,
          });
        }
      }

      utils.pipeline.getTicketPositions.invalidate({ merchantId, pipelineId });
      utils.pipeline.listFull.invalidate({ merchantId });
      toast.success("Estados de tickets actualizados");
      setIsEditing(false);
    } catch (err) {
      toast.error("Error al actualizar estados de tickets", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const addPositionToStage = (stage: "pending" | "success" | "failure") => {
    setEditPositions((prev) => ({
      ...prev,
      [stage]: [...prev[stage], { id: "", displayName: "", isNew: true }],
    }));
  };

  const removePositionFromStage = (stage: "pending" | "success" | "failure", index: number) => {
    setEditPositions((prev) => ({
      ...prev,
      [stage]: prev[stage].filter((_, i) => i !== index),
    }));
  };

  const updatePositionInStage = (
    stage: "pending" | "success" | "failure",
    index: number,
    field: "id" | "displayName",
    value: string
  ) => {
    setEditPositions((prev) => {
      const list = [...prev[stage]];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, [stage]: list };
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="size-4 animate-spin" />
        Cargando estados de tickets...
      </div>
    );
  }

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-gray-700">Estados de Tickets</p>
        {isEditing ? (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              onClick={handleSaveEdit}
              disabled={isSaving}
            >
              {isSaving && <Loader2 className="size-3 animate-spin" />}
              Guardar
            </Button>
            <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
              Cancelar
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="ghost" onClick={handleStartEdit}>
            <Edit2 className="size-3" />
            Editar
          </Button>
        )}
      </div>
      <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {STAGE_ORDER.map((stage) => {
            const canEdit = stage === "pending" || stage === "success" || stage === "failure";
            const isEditStage = canEdit && isEditing;
            const stagePositions = isEditStage
              ? editPositions[stage]
              : ((positionsByStage?.[stage] as TicketPosition[] | undefined) ?? []);

            return (
              <div key={stage} className="flex flex-col gap-2">
                <p className="text-sm font-semibold text-gray-700">{STAGE_LABELS[stage]}</p>
                {isEditStage
                  ? editPositions[stage].map((pos, index) => (
                      <div
                        key={pos.isNew ? `new-${index}` : pos.id}
                        className={cn(
                          "rounded-md border bg-white p-2",
                          STAGE_BORDER_COLORS[stage] ?? "border-gray-200"
                        )}
                      >
                        <div className="flex flex-col gap-1">
                          {!pos.isSystem ? (
                            <>
                              <Input
                                placeholder="id (ej: enRevision)"
                                value={pos.id}
                                className="h-6 px-1 text-xs"
                                onChange={(e) =>
                                  updatePositionInStage(
                                    stage,
                                    index,
                                    "id",
                                    e.target.value.replace(/\s/g, "")
                                  )
                                }
                                disabled={!pos.isNew}
                              />
                              <Input
                                placeholder="Nombre (ej: En revisión)"
                                value={pos.displayName}
                                className="h-6 px-1 text-xs"
                                onChange={(e) =>
                                  updatePositionInStage(stage, index, "displayName", e.target.value)
                                }
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 justify-start px-1 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                                onClick={() => removePositionFromStage(stage, index)}
                              >
                                <Trash2 className="size-3" />
                                Quitar
                              </Button>
                            </>
                          ) : (
                            <>
                              <p className="font-mono text-xs text-gray-600">{pos.id}</p>
                              <p className="text-sm font-medium text-gray-700">{pos.displayName}</p>
                            </>
                          )}
                        </div>
                      </div>
                    ))
                  : (stagePositions as TicketPosition[]).map((pos) => (
                      <div
                        key={pos.id}
                        className={cn(
                          "rounded-md border bg-white p-2",
                          pos.isSystem ? "border-gray-300" : "border-blue-200"
                        )}
                      >
                        <div className="flex flex-col gap-1">
                          <p className="font-mono text-xs text-gray-600">{pos.id}</p>
                          <p className="text-sm font-medium text-gray-700">{pos.displayName}</p>
                        </div>
                      </div>
                    ))}
                {/* End states are locked (closed_success / closed_failure) —
                    custom statuses can only be added as intermediate states. */}
                {stage === "pending" && isEditing && (
                  <Button
                    size="sm"
                    variant="outline"
                    className={cn("w-full", STAGE_ADD_BUTTON_CLASSES[stage])}
                    onClick={() => addPositionToStage(stage)}
                  >
                    <Plus className="size-3" />
                    Añadir estado
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TicketPositionBlock;
